import { WSOL } from '@raydium-io/raydium-sdk';
import {
    ComputeBudgetProgram,
    ConfirmedSignatureInfo,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    SystemProgram,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { useEffect, useState } from 'react';
import {
    pumpswapBuyUnitLimit,
    volumeMakerPumpswapDefaultJitoTip,
    volumeMakerPumpswapFeeReserve,
    volumeMakerPumpswapMaxPriorityFeeJitoLamports,
    volumeMakerPumpswapMaxPriorityFeeRPCLamports,
    volumeMakerPumpswapMinPriorityFeeJitoLamports,
    volumeMakerPumpswapMinPriorityFeeRPCLamports,
} from '../../../ConfigFees';
import { usePumpswap } from '../../hooks/usePumpswap';
import { useSolana } from '../../hooks/useSolana';
import { useJito } from '../../hooks/useJito';
import {
    createAssociatedTokenAccountIdempotentInstruction,
    createAssociatedTokenAccountInstruction,
    createCloseAccountInstruction,
    createSyncNativeInstruction,
    getAccount,
    getAssociatedTokenAddressSync,
    getMint,
    getOrCreateAssociatedTokenAccount,
    TOKEN_2022_PROGRAM_ID,
    TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import BN from 'bn.js';
import { volumeMakerV2CheckPoolBalanceLpSupplyUi, volumeMakerV2CheckPoolBalanceSolUi } from '../../../ConfigConstants';

export function VolumeMakerPumpswap({
    tokenDeployer,
    logMessage,
    connection,
    volumeBuyWallets,
    autoAction,
    updatedAt,
    volFunder,
    deployedToken,
}: {
    tokenDeployer: Keypair;
    logMessage: (message: string, type?: 'success' | 'error' | 'warn') => void;
    connection: Connection;
    volumeBuyWallets: Keypair[];
    autoAction: string;
    updatedAt: string;
    volFunder: Keypair | undefined;
    deployedToken: string;
}) {
    const { getSellIx, getPoolId, calculateQuoteFromBase, getLpMint, getPoolBaseTokenBalance, getBuyIx } = usePumpswap();
    const { getMultipleSolBalances } = useSolana();
    const { sendBundle, spamAndConfirmBundle } = useJito();
    const [useUniversalCSV, setUseUniversalCSV] = useState<boolean>(true);
    const [loopInfinitely, setLoopInfinitely] = useState<boolean>(true);

    const orderOptions = ['Sequential', 'Random'];
    const [order, setOrder] = useState<string>(orderOptions[1]);

    const [minBuyPercent, setMinBuyPercent] = useState<number>(98);
    const [maxBuyPercent, setMaxBuyPercent] = useState<number>(99);
    const [minDelay, setMinDelay] = useState<number>(400);
    const [maxDelay, setMaxDelay] = useState<number>(500);

    const [checkLP, setCheckLP] = useState<boolean>(true);
    const [randomBuyLock, setRandomBuyLock] = useState(false);
    const [randomBuyIntervalTimer, setRandomBuyIntervalTimer] = useState<NodeJS.Timer | null>(null);

    const [sequentialCurrentIndex, setSequentialCurrentIndex] = useState<number>(0);

    const [initBalance, setInitBalance] = useState<number>(0);
    const [currentBalance, setCurrentBalance] = useState<number>(0);

    const [jitoTip, setJitoTip] = useState<number>(volumeMakerPumpswapDefaultJitoTip / LAMPORTS_PER_SOL);

    const [jitoTransaction, setJitoTransaction] = useState<boolean>(false);
    const [volumeBuySlippagePercent, setVolumeBuySlippagePercent] = useState<number>(30);

    const handleNumberChange = (setter: React.Dispatch<React.SetStateAction<number>>, min: number, max: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);
        if (value < min) value = min;
        if (value > max) value = max;
        setter(value);
    };

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);
    useEffect(() => {
        if (autoActionStarted) return;
        if (!volumeBuyWallets.length) return;

        logMessage('Auto action updated', 'success');
        const timeDifference = Date.now() - new Date(updatedAt).getTime();
        const actionDetails = autoAction.split(':');
        const action = actionDetails[0];
        const minBuyPercentParam = Number(actionDetails[1]) || minBuyPercent;
        const maxBuyPercentParam = Number(actionDetails[2]) || maxBuyPercent;
        const minDelayParam = Number(actionDetails[3]) || minDelay;
        const maxDelayParam = Number(actionDetails[4]) || maxDelay;

        setMinBuyPercent(minBuyPercentParam);
        setMaxBuyPercent(maxBuyPercentParam);
        setMinDelay(minDelayParam);
        setMaxDelay(maxDelayParam);

        if (timeDifference > 30000) return;

        if (action === 'volumeBuy') {
            logMessage('Starting auto action for volume maker.');
            setAutoActionStarted(true);
            switchRandomBuy(minBuyPercentParam, maxBuyPercentParam, minDelayParam, maxDelayParam);
        }
    }, [deployedToken, autoAction, autoActionStarted]);

    useEffect(() => {
        const fetchTotalBalance = async () => {
            try {
                if (useUniversalCSV) {
                    const balances = await getMultipleSolBalances(
                        connection,
                        volumeBuyWallets.map((keypair) => keypair.publicKey),
                    );
                    const totalBalance = balances.reduce((acc, balance) => acc + balance, 0);
                    setCurrentBalance(totalBalance);
                    if (initBalance === 0) {
                        setInitBalance(totalBalance);
                    }
                }
            } catch (error) {
                console.log('Error fetching total balance', error);
                logMessage('Error fetching total balance', 'error');
            }
        };

        const intervalId = setInterval(fetchTotalBalance, 2000);
        return () => {
            clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volumeBuyWallets, useUniversalCSV]);

    const switchRandomBuy = async (minBuyPercent: number, maxBuyPercent: number, minDelay: number, maxDelay: number) => {
        if (randomBuyIntervalTimer) {
            clearInterval(randomBuyIntervalTimer);
            setRandomBuyIntervalTimer(null);
            setRandomBuyLock(false);
        } else {
            setRandomBuyLock(true);
            let sequentialIndex = sequentialCurrentIndex;
            let randomIteration = 0;
            const intervalId = setInterval(
                async () => {
                    try {
                        //sequential
                        if (order === orderOptions[0]) {
                            console.log('index', sequentialIndex);
                            const wallet = volumeBuyWallets[sequentialIndex];
                            const walletBalance = await connection.getBalance(wallet.publicKey, 'processed');
                            console.log('walletBalance', walletBalance / LAMPORTS_PER_SOL);
                            const buyValue = Math.floor(
                                ((walletBalance - volumeMakerPumpswapFeeReserve) * (Math.random() * (maxBuyPercent - minBuyPercent) + minBuyPercent)) / 100,
                            );
                            console.log('buyValue', buyValue / LAMPORTS_PER_SOL);
                            buySingle(connection, wallet, buyValue);
                            sequentialIndex++;
                            setSequentialCurrentIndex(sequentialIndex);
                            if (sequentialIndex >= volumeBuyWallets.length) {
                                sequentialIndex = 0;
                                if (!loopInfinitely) {
                                    clearInterval(intervalId);
                                    setRandomBuyLock(false);
                                    setSequentialCurrentIndex(0);
                                    setRandomBuyIntervalTimer(null);
                                }
                            }
                        }
                        //random
                        else if (order === orderOptions[1]) {
                            const solBalances = await getMultipleSolBalances(
                                connection,
                                volumeBuyWallets.map((wallet) => wallet.publicKey),
                            );
                            console.log('solBalances', solBalances);
                            //console.debug('token balances', solBalances);
                            const walletsWithPositiveBalance = volumeBuyWallets.filter((wallet, index) => solBalances[index] > volumeMakerPumpswapFeeReserve);
                            //console.debug('Wallets with positive balance', walletsWithPositiveBalance);
                            if (walletsWithPositiveBalance.length == 0) {
                                console.warn('No wallets with positive balance');
                                logMessage('No wallets with positive balance left', 'error');
                                return;
                            }
                            const randomIndex = Math.floor(Math.random() * (walletsWithPositiveBalance.length - 1));
                            //console.debug('Random index', randomIndex);
                            const randomWallet = walletsWithPositiveBalance[randomIndex];
                            const randomWalletBalance = await connection.getBalance(randomWallet.publicKey, 'processed');
                            //console.debug('Random wallet', randomWallet.publicKey.toBase58());
                            const buyValue = Math.floor(
                                ((randomWalletBalance - volumeMakerPumpswapFeeReserve) * (Math.random() * (maxBuyPercent - minBuyPercent) + minBuyPercent)) /
                                    100,
                            );
                            buySingle(connection, randomWallet, buyValue);
                            randomIteration++;
                            if (randomIteration >= volumeBuyWallets.length) {
                                if (!loopInfinitely) {
                                    clearInterval(intervalId);
                                    setRandomBuyLock(false);
                                    setRandomBuyIntervalTimer(null);
                                }
                            }
                        }
                    } catch (error) {
                        console.log('Error with buy', error);
                        logMessage('Error with buy', 'error');
                    }
                },
                minDelay + Math.random() * (maxDelay - minDelay),
            );
            setRandomBuyIntervalTimer(intervalId);
            logMessage('Random Buy enabled', 'success');
        }
    };

    async function buySingle(connection: Connection, wallet: Keypair, solAmount: number) {
        console.log('Buying', solAmount / LAMPORTS_PER_SOL, 'SOL from', wallet.publicKey.toBase58());

        try {
            if (checkLP) {
                const poolId = await getPoolId(tokenDeployer.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
                console.log('poolId', poolId.toBase58());
                const lpMint = await getLpMint(connection, poolId);
                console.log('lpMint', lpMint.toBase58());
                const lpTokenSupplyUi = await getMint(connection, lpMint, 'processed', TOKEN_2022_PROGRAM_ID).then((mint) => Number(mint.supply));
                const poolSolBalance = await getPoolBaseTokenBalance(connection, poolId);

                console.log('poolSolBalance', poolSolBalance);
                console.log('lpTokenSupplyUi', lpTokenSupplyUi);

                if (Number(poolSolBalance) < volumeMakerV2CheckPoolBalanceSolUi * LAMPORTS_PER_SOL) {
                    logMessage(`Pool balance is less than ${volumeMakerV2CheckPoolBalanceSolUi} SOL. Skipping...`, 'error');
                    //switchRandomBuy();
                    return;
                }
                if (lpTokenSupplyUi! < volumeMakerV2CheckPoolBalanceLpSupplyUi) {
                    logMessage(`LP token supply is less than ${volumeMakerV2CheckPoolBalanceLpSupplyUi}. Skipping...`, 'error');
                    //switchRandomBuy();
                    return;
                }
            }

            const min = jitoTransaction ? volumeMakerPumpswapMinPriorityFeeJitoLamports : volumeMakerPumpswapMinPriorityFeeRPCLamports;
            const max = jitoTransaction ? volumeMakerPumpswapMaxPriorityFeeJitoLamports : volumeMakerPumpswapMaxPriorityFeeRPCLamports;
            const priorityFeeLamports = Math.floor(Math.random() * (max - min) + min);

            const poolId = await getPoolId(tokenDeployer.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));

            console.log('sol amount', solAmount);

            const tokenAta = getAssociatedTokenAddressSync(new PublicKey(deployedToken), wallet.publicKey);
            const wsolAta = getAssociatedTokenAddressSync(new PublicKey(WSOL.mint), wallet.publicKey);
            const accIxs = [
                createAssociatedTokenAccountIdempotentInstruction(wallet.publicKey, wsolAta, wallet.publicKey, new PublicKey(WSOL.mint)),
                SystemProgram.transfer({
                    fromPubkey: wallet.publicKey,
                    toPubkey: wsolAta,
                    lamports: BigInt(solAmount + 10001),
                }),
                createSyncNativeInstruction(wsolAta),
                createAssociatedTokenAccountIdempotentInstruction(wallet.publicKey, tokenAta, wallet.publicKey, new PublicKey(deployedToken)),
            ];
            const minQuoteAmountOut = await calculateQuoteFromBase(connection, poolId, new BN(solAmount), volumeBuySlippagePercent);

            if (Number(minQuoteAmountOut.toString()) < 1000000000) {
                logMessage('Estimated token receive amount is lower than 1000.000000, aborting...', 'error');
                return;
            }

            const sellIx = getSellIx(
                poolId,
                new PublicKey(WSOL.mint),
                new PublicKey(deployedToken),
                wallet.publicKey,
                BigInt(solAmount),
                BigInt(minQuoteAmountOut.toString()),
            );
            const priorityFeeIx = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamports });

            const message = new TransactionMessage({
                payerKey: wallet.publicKey,
                recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
                instructions: [...accIxs, sellIx, priorityFeeIx],
            }).compileToV0Message();
            const sellTx = new VersionedTransaction(message);
            sellTx.sign([wallet]);

            const hash = await connection.sendTransaction(sellTx, { skipPreflight: true });
            logMessage(`Transaction sent: ${hash}, sol amount ${solAmount / LAMPORTS_PER_SOL}`, 'success');
            // await spamAndConfirmBundle(connection, [sellTx], wallet, jitoTip * LAMPORTS_PER_SOL, false, 1000, 30000, false)
            //     .then(() => {
            //         logMessage(`Transaction sent: ${(solAmount / LAMPORTS_PER_SOL).toFixed(3)} SOL from ${wallet.publicKey.toBase58()}`, 'success');
            //     })
            //     .catch((error) => {
            //         console.error(error);
            //     });
        } catch (error) {
            logMessage(`Error buying wallet from ${wallet.publicKey.toBase58()}`);
            console.log('Error buying wallet from', wallet.publicKey.toBase58());
            console.error(error);
        }
    }

    async function getFirstTransaction(address: PublicKey) {
        let before: string | undefined = undefined;
        let allSignatures: ConfirmedSignatureInfo[] = [];

        while (true) {
            const signatureInfos: ConfirmedSignatureInfo[] = await connection.getSignaturesForAddress(
                address,
                {
                    before,
                    limit: 1000,
                },
                'confirmed',
            );
            const signatures = signatureInfos.map((s) => s.signature);

            if (signatures.length === 0) break;

            allSignatures.push(...signatureInfos);
            before = signatures[signatures.length - 1];

            if (signatures.length < 1000) break;
        }

        if (allSignatures.length === 0) {
            console.log('No transactions found');
            return;
        }

        const oldestSignature = allSignatures[allSignatures.length - 1].signature;
        const tx = await connection.getTransaction(oldestSignature, {
            maxSupportedTransactionVersion: 0,
            commitment: 'confirmed',
        });

        console.log('Oldest Transaction:', tx);
        return tx;
    }

    const [minKillKiwiDelay, setKillKiwiMinDelay] = useState(1000);
    const [maxKillKiwiDelay, setKillKiwiMaxDelay] = useState(10000);
    const [minKillKiwiBuyAmount, setMinKillKiwiBuyAmount] = useState(0.01);
    const [maxKillKiwiBuyAmount, setMaxKillKiwiBuyAmount] = useState(0.02);
    const [killKiwiStartIndex, setKillKiwiStartIndex] = useState(0);

    async function killKiwi(kiwi: PublicKey, fucker: PublicKey | undefined) {
        // while (true) {
        logMessage(`Killing ${kiwi.toBase58()}`);
        const kiwiTokens = (
            await connection.getTokenAccountsByOwner(
                kiwi,
                {
                    programId: TOKEN_PROGRAM_ID,
                },
                'processed',
            )
        ).value.map((i) => i.pubkey);
        console.log('Kiwi tokens:', kiwiTokens.length);

        const randomStartIndex = Math.floor(Math.random() * (kiwiTokens.length - 2000));
        const TXS_PER_WALLET = 1;
        let txCount = 0;
        let index = killKiwiStartIndex;
        for (let i = randomStartIndex; i < kiwiTokens.length; i++) {
            try {
                const solAmount =
                    Math.floor(Math.random() * (maxKillKiwiBuyAmount - minKillKiwiBuyAmount) * LAMPORTS_PER_SOL) + minKillKiwiBuyAmount * LAMPORTS_PER_SOL;
                const wallet = volumeBuyWallets[index];
                console.log('tokenAccount:', kiwiTokens[i].toBase58());
                const account = await getAccount(connection, kiwiTokens[i]);
                const mint = await getMint(connection, account.mint, 'processed');

                if (fucker) {
                    const fuckerAta = getAssociatedTokenAddressSync(mint.address, fucker);
                    const fuckerBalance = await connection
                        .getTokenAccountBalance(fuckerAta, 'processed')
                        .then((res) => res.value.amount)
                        .catch(() => '0');
                    if (fuckerBalance !== '0') {
                        logMessage(`Fucker also has tokens, skipping...`, 'error');
                        continue;
                    }
                }
                const creationTxSignature = await getFirstTransaction(mint.address);
                if (!creationTxSignature) {
                    console.error('No creation transaction found for mint', mint.address.toBase58());
                    continue;
                }
                console.log('creationTxSignature:', creationTxSignature);
                const tx = await connection.getTransaction(creationTxSignature.transaction.signatures[0], {
                    maxSupportedTransactionVersion: 0,
                    commitment: 'confirmed',
                });
                const keys = tx?.transaction.message.getAccountKeys();
                if (!keys) {
                    console.error('No keys found in transaction');
                    continue;
                }
                const maker = keys.staticAccountKeys[0];
                console.log('mint', account.mint.toBase58());
                console.log('Token maker:', maker.toBase58());

                const min = jitoTransaction ? volumeMakerPumpswapMinPriorityFeeJitoLamports : volumeMakerPumpswapMinPriorityFeeRPCLamports;
                const max = jitoTransaction ? volumeMakerPumpswapMaxPriorityFeeJitoLamports : volumeMakerPumpswapMaxPriorityFeeRPCLamports;
                const priorityFeeLamports = Math.floor(Math.random() * (max - min) + min);
                const priorityFeeIx = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamports });

                const poolId = await getPoolId(maker, new PublicKey(WSOL.mint), new PublicKey(mint.address));
                const { blockhash } = await connection.getLatestBlockhash('finalized');

                const tokenAta = getAssociatedTokenAddressSync(new PublicKey(mint.address), wallet.publicKey);
                const wsolAta = getAssociatedTokenAddressSync(new PublicKey(WSOL.mint), wallet.publicKey);

                const initIxs = [
                    createAssociatedTokenAccountIdempotentInstruction(tokenDeployer.publicKey, wsolAta, wallet.publicKey, new PublicKey(WSOL.mint)),
                    SystemProgram.transfer({
                        fromPubkey: tokenDeployer.publicKey,
                        toPubkey: wsolAta,
                        lamports: BigInt(solAmount + 10001),
                    }),
                    createSyncNativeInstruction(wsolAta),
                    createAssociatedTokenAccountIdempotentInstruction(tokenDeployer.publicKey, tokenAta, wallet.publicKey, new PublicKey(mint.address)),
                    priorityFeeIx,
                ];
                const initTx = new VersionedTransaction(
                    new TransactionMessage({
                        payerKey: tokenDeployer.publicKey,
                        recentBlockhash: blockhash,
                        instructions: initIxs,
                    }).compileToV0Message(),
                );
                initTx.sign([tokenDeployer]);

                const minQuoteAmountOut = await calculateQuoteFromBase(connection, poolId, new BN(solAmount), volumeBuySlippagePercent);
                const sellIx = getSellIx(
                    poolId,
                    new PublicKey(WSOL.mint),
                    new PublicKey(mint.address),
                    wallet.publicKey,
                    BigInt(solAmount),
                    BigInt(minQuoteAmountOut.toString()),
                );

                const sellTx = new VersionedTransaction(
                    new TransactionMessage({
                        payerKey: tokenDeployer.publicKey,
                        recentBlockhash: blockhash,
                        instructions: [sellIx, priorityFeeIx],
                    }).compileToV0Message(),
                );
                sellTx.sign([wallet, tokenDeployer]);

                const buyTx = new VersionedTransaction(
                    new TransactionMessage({
                        payerKey: tokenDeployer.publicKey,
                        recentBlockhash: blockhash,
                        instructions: [
                            getBuyIx(
                                poolId,
                                new PublicKey(WSOL.mint),
                                new PublicKey(mint.address),
                                wallet.publicKey,
                                BigInt(Math.floor(solAmount * 0.9974).toString()),
                                BigInt(Number.MAX_SAFE_INTEGER),
                            ),
                            createCloseAccountInstruction(wsolAta, tokenDeployer.publicKey, wallet.publicKey),
                            ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamports }),
                        ],
                    }).compileToV0Message(),
                );
                buyTx.sign([wallet, tokenDeployer]);

                spamAndConfirmBundle(connection, [initTx, sellTx, buyTx], tokenDeployer, 10000)
                    .then(() => {
                        logMessage(`bundle confirmed`, 'success');
                    })
                    .catch((error) => {
                        console.error(error);
                    });

                // console.log(await connection.sendTransaction(initTx, { skipPreflight: true }));
                // await new Promise((resolve) => setTimeout(resolve, 3000));
                // console.log(await connection.sendTransaction(sellTx, { skipPreflight: true }));
                // await new Promise((resolve) => setTimeout(resolve, 3000));
                // console.log(await connection.sendTransaction(buyTx, { skipPreflight: true }));
                // await new Promise((resolve) => setTimeout(resolve, 3000));

                txCount++;
                if (txCount >= TXS_PER_WALLET) {
                    txCount = 0;
                    index++;
                    logMessage(`Spamming started on ${index} wallets. Current wallet ${volumeBuyWallets[index].publicKey.toBase58()}`, 'success');
                }

                const sleepTime = minKillKiwiDelay + Math.random() * (maxKillKiwiDelay - minKillKiwiDelay);
                await new Promise((resolve) => setTimeout(resolve, sleepTime));
            } catch (error) {
                console.error(error);
                //break;
            }
        }
    }

    return (
        <section>
            <section className='csv'>
                <div className='universal-csv-enable'>
                    <input type='checkbox' checked={useUniversalCSV} onChange={() => setUseUniversalCSV(!useUniversalCSV)} disabled={true || randomBuyLock} />
                    Universal CSV
                    <input type='file' accept='.csv' id='fileInput' disabled={true || randomBuyLock} />
                    <label className='file-input-label' htmlFor='fileInput'>
                        📁 <span>Upload a CSV</span>
                    </label>
                </div>
                <div className='order'>
                    Order
                    <label className='radio'>
                        <input
                            type='radio'
                            name='mode'
                            value='Sequential'
                            checked={order === orderOptions[0]}
                            onChange={() => setOrder(orderOptions[0])}
                            disabled={randomBuyLock}
                        />
                        <span>&nbsp;&nbsp;Sequential</span>
                    </label>
                    <label className='radio'>
                        <input
                            type='radio'
                            name='mode'
                            value='Random'
                            checked={order === orderOptions[1]}
                            onChange={() => setOrder(orderOptions[1])}
                            disabled={randomBuyLock}
                        />
                        <span>&nbsp;&nbsp;Random</span>
                    </label>
                </div>
                <div className='example-btn'>
                    <a href='#' className='example-csv-link'>
                        Example CSV
                    </a>
                </div>
            </section>
            <section className='buy-range-time-delay'>
                <div className='buy-range'>
                    Buy Range
                    <input type='number' value={minBuyPercent} onChange={handleNumberChange(setMinBuyPercent, 0, 100)} disabled={randomBuyLock} />
                    to
                    <input type='number' value={maxBuyPercent} onChange={handleNumberChange(setMaxBuyPercent, 0, 100)} disabled={randomBuyLock} />%
                </div>
                <div className='time-delay'>
                    Time Delay
                    <input type='number' value={minDelay} onChange={handleNumberChange(setMinDelay, 0, Number.MAX_VALUE)} disabled={randomBuyLock} />
                    to
                    <input type='number' value={maxDelay} onChange={handleNumberChange(setMaxDelay, 0, Number.MAX_VALUE)} disabled={randomBuyLock} />
                    ms
                </div>
                <div className='jito-tip'>
                    JitoTip
                    <input
                        type='number'
                        value={jitoTip}
                        onChange={(e) => {
                            setJitoTip(Number(e.target.value));
                        }}
                    />
                </div>
            </section>
            <section className='inputs'>
                <div className='loop-check'>
                    <div className='name-check-inp-case'>
                        <div className='loop-inp-check'>
                            <input type='checkbox' checked={loopInfinitely} onChange={() => setLoopInfinitely(!loopInfinitely)} disabled={randomBuyLock} />
                            <p>Loop Infinitely</p>
                        </div>
                        <div className='lp-protection-check-inp'>
                            <input type='checkbox' checked={checkLP} onChange={() => setCheckLP(!checkLP)} disabled={randomBuyLock} />
                            <p>Check LP Protection</p>
                        </div>
                        <div className='jito-tx-check-inp'>
                            <input
                                type='checkbox'
                                checked={jitoTransaction}
                                onClick={() => setJitoTransaction((prev) => !prev)}
                                disabled={randomBuyLock || true}
                            />
                            <p>Jito TX</p>
                        </div>
                    </div>
                    <div className='slip-name-inp'>
                        <p>Slippage</p>
                        <input value={volumeBuySlippagePercent} onChange={(e) => setVolumeBuySlippagePercent(Number(e.target.value))} />
                    </div>
                </div>
            </section>
            <section className='switch-precreate'>
                <label>
                    <div className='switch'>
                        <input
                            type='checkbox'
                            checked={randomBuyLock}
                            onClick={() => switchRandomBuy(minBuyPercent, maxBuyPercent, minDelay, maxDelay)}
                            // onChange={({ target: { checked } }: { target: { checked: boolean } }) => setRandomBuyLock(checked)}
                            disabled={!deployedToken || !volFunder || !volumeBuyWallets.length}
                        />
                        <span className='slider round'></span>
                        Pause/Start
                    </div>
                </label>
                <div className='precreate'>
                    <button className='button-accent'>Precreate ATA</button>
                </div>
                <div className='info'>
                    <div>{volumeBuyWallets.length} Buy Volume Wallets Detected</div>
                    <div className='text'>Initial: {(initBalance / LAMPORTS_PER_SOL).toFixed(2)}</div>
                    <div className='text'>Current: {(currentBalance / LAMPORTS_PER_SOL).toFixed(2)}</div>
                </div>
            </section>

            <div className='kill-kiwi-value'>
                <div style={{ marginBottom: '8px' }}>
                    <button
                        style={{ marginRight: '8px' }}
                        onClick={() => {
                            killKiwi(
                                new PublicKey('kiwiC4pg5mC4N5AhpXc4Av3V6oV7Sn2p3CqB7NeHbJJ'),
                                new PublicKey('EdSg1GRqe5bEWxkh7FgqV3RF5kQYaQ8NEutBdiBTXass'),
                            );
                        }}
                    >
                        KILL KIWI
                    </button>
                    <button
                        style={{ marginRight: '8px' }}
                        onClick={() => {
                            killKiwi(new PublicKey('EdSg1GRqe5bEWxkh7FgqV3RF5kQYaQ8NEutBdiBTXass'), undefined);
                        }}
                    >
                        KILL EDSG
                    </button>
                    <button
                        onClick={() => {
                            killKiwi(
                                new PublicKey('CLf3PUt3be4qiqEBrXcg4U6S4Jjpz7RW8udwvc2NqR8J'),
                                new PublicKey('EdSg1GRqe5bEWxkh7FgqV3RF5kQYaQ8NEutBdiBTXass'),
                            );
                        }}
                    >
                        KILL CLF3
                    </button>

                    <span style={{ marginLeft: '8px' }}>
                        Start Index:
                        <input
                            type='number'
                            value={killKiwiStartIndex}
                            onChange={(e) => setKillKiwiStartIndex(Number(e.target.value))}
                            placeholder='Start Index'
                            style={{ marginLeft: '4px', width: '80px' }}
                        />
                    </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <label>
                        Min Delay (ms):
                        <input
                            type='number'
                            value={minKillKiwiDelay}
                            onChange={(e) => setKillKiwiMinDelay(Number(e.target.value))}
                            placeholder='Min Delay'
                            style={{ marginLeft: '4px' }}
                        />
                    </label>

                    <label>
                        Max Delay (ms):
                        <input
                            type='number'
                            value={maxKillKiwiDelay}
                            onChange={(e) => setKillKiwiMaxDelay(Number(e.target.value))}
                            placeholder='Max Delay'
                            style={{ marginLeft: '4px' }}
                        />
                    </label>

                    <label>
                        Min Buy Amount:
                        <input
                            type='number'
                            value={minKillKiwiBuyAmount}
                            onChange={(e) => setMinKillKiwiBuyAmount(Number(e.target.value))}
                            placeholder='Min Buy Amount'
                            style={{ marginLeft: '4px' }}
                        />
                    </label>

                    <label>
                        Max Buy Amount:
                        <input
                            type='number'
                            value={maxKillKiwiBuyAmount}
                            onChange={(e) => setMaxKillKiwiBuyAmount(Number(e.target.value))}
                            placeholder='Max Buy Amount'
                            style={{ marginLeft: '4px' }}
                        />
                    </label>
                </div>
            </div>
        </section>
    );
}
